import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ElfsightWidget from '../components/about/ElfsightWidget';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { useScrollReveal } from '../hooks/use-scroll-reveal';
import heroImage from '../assets/hero-ai-transition.jpg';
import stallBg from '../assets/ai-stall-bg.jpg';
import workBg from '../assets/ai-work-bg.jpg';
import ctaBg from '../assets/ai-cta-bg.jpg';

const handleScheduleCall = () => {
  window.open('https://calendly.com/kentfrazier/30-minutes', '_blank');
};

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-4 mb-8">
    <span className="h-px w-12 bg-primary" aria-hidden="true" />
    <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
      {children}
    </p>
  </div>
);

const Hero = () => {
  const content = useScrollReveal<HTMLDivElement>();
  const tail = useScrollReveal<HTMLDivElement>();
  return (
    <>
    {/* Hero — one declaration, one question, one quiet comparison */}
    <section className="relative overflow-hidden bg-foreground">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt=""
          className="w-full h-full object-cover object-center opacity-[0.14]"
          loading="eager"
        />
      </div>

      <div className="container-mobile relative z-10 max-w-7xl mx-auto py-14 sm:py-20 lg:py-24">
        <div className="flex items-center mb-10 sm:mb-14 text-[0.68rem] tracking-[0.3em] uppercase font-semibold">
          <span className="h-px w-8 sm:w-12 bg-primary" aria-hidden="true" />
          <span className="ml-4 text-primary">AI Transformation</span>
        </div>

        <div
          ref={content.ref}
          className={`transition-all duration-1000 ${
            content.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h1 className="max-w-[17ch] text-background text-[2.45rem] sm:text-5xl lg:text-7xl leading-[1.04] sm:leading-[1.02]" style={{ textWrap: 'balance' }}>
            <span className="text-background/55 italic">Something</span> is building the future.
            <span className="block mt-3 sm:mt-5 text-primary italic">Someone answers for it.</span>
          </h1>

          <div className="mt-10 sm:mt-12 lg:mt-14 max-w-5xl grid sm:grid-cols-[minmax(0,0.8fr)_minmax(20rem,1fr)] gap-7 sm:gap-10 lg:gap-14 items-center">
            <p className="font-display italic text-background text-xl sm:text-2xl leading-snug sm:pb-1">
              Where are you investing?
            </p>

            <div className="border-l border-primary pl-5 sm:pl-7 py-1 space-y-3">
              <p className="text-background/55 text-sm sm:text-base leading-relaxed">
                The <span className="italic">machine</span> is updated and commoditizes knowledge.
              </p>
              <p className="text-background text-sm sm:text-base leading-relaxed">
                The <span className="text-primary italic font-display text-base sm:text-lg">human</span> is developed and compounds wisdom.
              </p>
            </div>
          </div>

        </div>
      </div>


    </section>

    {/* Threefold — triptych contained within the main page margins */}
    <section
      id="threefold-call"
      ref={tail.ref}
      className={`relative bg-background transition-all duration-1000 ${
        tail.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      <div className="container-mobile max-w-7xl mx-auto pt-16 sm:pt-24 pb-16 sm:pb-24">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <h2
            className="text-foreground text-3xl sm:text-4xl lg:text-5xl leading-[1.1]"
            style={{ textWrap: 'balance' }}
          >
            Working at the <span className="text-primary italic">edge</span>...
          </h2>
          <p className="mt-5 text-foreground/70 text-base sm:text-lg leading-relaxed max-w-2xl">
            How do you choose?
          </p>
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)] items-center gap-12 lg:gap-16">
          <div className="relative w-full max-w-[42rem] aspect-square mx-auto [container-type:inline-size]" aria-label="Three concentric choices">
            <div
              className="absolute inset-0 rounded-full bg-foreground shadow-xl overflow-hidden"
              style={{
                backgroundImage:
                  'linear-gradient(hsl(var(--background) / 0.035) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--background) / 0.035) 1px, transparent 1px)',
                backgroundSize: '32px 32px',
              }}
            >
              <div className="absolute inset-x-[12%] top-[2.5%] text-center">
                <span className="text-background/90 font-semibold tracking-[0.28em] uppercase block mb-[0.35cqw] text-[2cqw]">
                  For the system
                </span>
                <p className="text-background font-display leading-[0.95] text-[4cqw]">
                  Delegate to <span className="text-background italic">machines</span>
                </p>
              </div>
            </div>

            <div className="absolute inset-[15%] rounded-full bg-secondary border border-sage/60 shadow-lg">
              <div className="absolute inset-x-[6%] top-[7%] text-center">
                <span className="text-muted-foreground font-semibold tracking-[0.26em] uppercase block mb-[0.8cqw] text-[2.1cqw]">
                  For your people
                </span>
                <p className="text-foreground font-display leading-none text-[4.4cqw]">
                  Delegate to <span className="text-primary italic">people</span>
                </p>
              </div>
            </div>

            <div className="absolute inset-[31%] rounded-full bg-primary shadow-xl flex items-center justify-center text-center p-[4%]">
              <div>
                <span className="text-background font-semibold tracking-[0.26em] uppercase block mb-[0.6cqw] text-[1.9cqw]">
                  For you
                </span>
                <p className="text-background font-display leading-[1.05] text-balance text-[4.8cqw]">
                  What is mine to <span className="italic">own</span>?
                </p>
                <p className="mt-[1.5cqw] text-background/90 leading-snug text-[2.1cqw]">
                  Attention. Choice. Accountability.
                </p>
              </div>
            </div>
          </div>


          <div className="lg:pl-4">
            <p className="text-sage font-semibold tracking-[0.3em] uppercase text-[0.65rem] mb-10">
              The relational field
            </p>

            <div className="relative border-t border-sage/70 pt-5 mb-12 lg:before:absolute lg:before:right-full lg:before:top-[-1px] lg:before:w-20 lg:before:border-t lg:before:border-sage/70">
              <p className="font-display text-foreground text-2xl sm:text-3xl leading-tight">
                Delegate to <span className="text-primary italic">machines</span>
              </p>
              <p className="mt-3 text-foreground/70 text-base leading-relaxed">
                So the work flows without a person in every loop.
              </p>
            </div>

            <div className="relative border-t border-sage/70 pt-5 mb-12 lg:before:absolute lg:before:right-full lg:before:top-[-1px] lg:before:w-20 lg:before:border-t lg:before:border-sage/70">
              <p className="font-display text-foreground text-2xl sm:text-3xl leading-tight">
                Delegate to <span className="text-primary italic">people</span>
              </p>
              <p className="mt-3 text-foreground/70 text-base leading-relaxed">
                So they grow into decisions they could not make yesterday.
              </p>
            </div>

            <div className="relative border-t border-sage/70 pt-5 lg:before:absolute lg:before:right-full lg:before:top-[-1px] lg:before:w-20 lg:before:border-t lg:before:border-sage/70">
              <p className="font-display text-foreground text-2xl sm:text-3xl leading-tight">
                What is mine to <span className="text-primary italic">own</span>?
              </p>
              <p className="mt-3 text-foreground/70 text-base leading-relaxed">
                AI changes the work, the role, and the person doing it. Engineers and leaders are being asked to become someone new while they build what comes next.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

const TheStall = () => {
  const stat = useScrollReveal<HTMLDivElement>();
  const body = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-secondary py-16 sm:py-36 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center opacity-[0.22] pointer-events-none"
        style={{ backgroundImage: `url(${stallBg})` }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-secondary/90 via-secondary/70 to-secondary pointer-events-none"
      />
      <div className="container-mobile max-w-6xl mx-auto relative z-10">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div
            ref={stat.ref}
            className={`md:col-span-5 transition-all duration-1000 ${
              stat.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <Eyebrow>Deployed. Unaccounted for.</Eyebrow>
            <div className="relative">
              <p
                className="font-display text-foreground tracking-tight leading-[1.05] mb-8"
                style={{ fontSize: 'clamp(2rem, 4.5vw, 3rem)' }}
              >
                The <span className="text-primary italic">question</span> the plan does not answer.
              </p>
              <span className="block h-px w-16 bg-primary mb-6" aria-hidden="true" />
              <p className="text-foreground/85 text-base sm:text-lg leading-relaxed max-w-[38ch]">
                The systems are in production and act on their own. Who answers for them?
              </p>
            </div>
          </div>
          <div
            ref={body.ref}
            className={`md:col-span-7 md:pl-10 md:border-l md:border-border transition-all duration-1000 delay-150 ${
              body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h2
              className="text-foreground text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-8"
              style={{ textWrap: 'balance' }}
            >
              The practice was the <span className="text-primary italic">teacher</span>.
            </h2>
            <div className="space-y-6 text-lg sm:text-xl text-foreground/90 leading-relaxed">
              <p>
                Twenty years of judgment came from twenty years of small failures, reviewed by someone who cared enough to explain them. That work goes to the machine now, and it comes back almost instantly. The output is better. The person is not.
              </p>
              <p>
                Somewhere in your company a leader is signing off on a system she does not understand, built by an engineer who never had to understand it either. Neither of them is lying. Both of them are exposed.
              </p>
              <p>
                Structure can name who is accountable. Capacity is what lets a person carry it.
              </p>
              <p className="font-display text-foreground text-xl sm:text-2xl italic">
                Developing human beings is a safety strategy.
              </p>
            </div>
            <div className="mt-10 sm:mt-12 pt-6 border-t border-border">
              <p className="text-foreground/75 text-sm sm:text-base leading-relaxed">
                Read the essay behind this:{" "}
                <Link
                  to="/essays/oversight-degrades-the-overseer"
                  onClick={() => window.scrollTo(0, 0)}
                  className="inline-flex items-center gap-1 font-display text-primary underline underline-offset-4 hover:text-primary/80 transition-colors"
                >
                  Oversight Degrades the Overseer
                  <ArrowRight className="h-3 w-3" />
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TheWork = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const loopOne = useScrollReveal<HTMLDivElement>();
  const loopTwo = useScrollReveal<HTMLDivElement>();
  const footer = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground py-16 sm:py-36 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center opacity-10 pointer-events-none"
        style={{ backgroundImage: `url(${workBg})` }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 80% 20%, hsl(var(--primary) / 0.18) 0%, transparent 50%)',
        }}
      />
      <div className="container-mobile relative z-10 max-w-6xl mx-auto">
        <div
          ref={heading.ref}
          className={`grid md:grid-cols-12 gap-8 md:gap-12 mb-16 sm:mb-24 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-primary" aria-hidden="true" />
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                The Work
              </p>
            </div>
            <h2
              className="text-background text-3xl sm:text-4xl lg:text-5xl leading-[1.1]"
              style={{ textWrap: 'balance' }}
            >
              Two loops. <span className="italic text-primary">One is ours.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 md:pt-2">
            <p className="text-background/90 text-lg leading-relaxed">
              Your team builds the machine loop. We develop the humans building and receiving what the machine creates: the engineers building the future, the people whose jobs and work identities are transforming, and the leaders accountable for what gets built.
            </p>
          </div>
        </div>

        {/* Diptych — two distinct cards */}
        <div
          ref={loopOne.ref}
          className={`grid md:grid-cols-2 gap-6 transition-all duration-1000 delay-100 ${
            loopOne.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* Loop — Deployment (light card) */}
          <article className="relative p-8 sm:p-12 md:p-14 flex flex-col rounded-2xl bg-background text-foreground shadow-2xl">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
              <span className="text-primary font-semibold tracking-[0.3em] uppercase text-[0.7rem]">
                The Machine Loop
              </span>
            </div>
            <p
              className="font-display text-foreground leading-[1.05] mb-6"
              style={{ fontSize: 'clamp(1.75rem, 3.2vw, 2.5rem)' }}
            >
              Where <span className="italic text-primary">capability</span> scales.
            </p>
            <p className="text-foreground/85 text-base sm:text-lg leading-relaxed mb-8">
              The workflows, decisions, and interfaces where AI creates real leverage. Your engineers, vendors, and integrators own this loop.
            </p>
            <div className="mt-auto pt-6 border-t border-border/60">
              <p className="text-foreground/60 text-[0.7rem] tracking-[0.2em] uppercase font-semibold mb-2">
                Built by your team
              </p>
              <p className="text-foreground/85 leading-relaxed">
                We develop the ones building it and the ones accountable for it. What gets built carries their judgment.
              </p>
            </div>
          </article>

          {/* Loop — Development (warm card) */}
          <article
            ref={loopTwo.ref}
            className="relative p-8 sm:p-12 md:p-14 flex flex-col rounded-2xl bg-primary text-background shadow-2xl"
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="h-2 w-2 rounded-full bg-background" aria-hidden="true" />
              <span className="text-background font-semibold tracking-[0.3em] uppercase text-[0.7rem]">
                The Human Loop
              </span>
            </div>
            <p
              className="font-display text-background leading-[1.05] mb-6"
              style={{ fontSize: 'clamp(1.75rem, 3.2vw, 2.5rem)' }}
            >
              Where <span className="italic text-foreground">capacity</span> grows.
            </p>
            <p className="text-background/90 text-base sm:text-lg leading-relaxed mb-8">
              The identity, trust, and capacity work that decides what gets built and whether it ever gets used. This is where the investment survives contact with your people.
            </p>
            <div className="mt-auto pt-6 border-t border-background/25">
              <p className="text-background/70 text-[0.7rem] tracking-[0.2em] uppercase font-semibold mb-2">
                Development inside deployment
              </p>
              <p className="text-background/90 leading-relaxed">
                Deliberate practices threaded into the rollout, while it goes live.
              </p>
            </div>
          </article>
        </div>

        {/* Footnote to the essay */}
        <div
          ref={footer.ref}
          className={`mt-14 sm:mt-20 pt-6 border-t border-background/20 transition-all duration-1000 delay-300 ${
            footer.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-background/80 text-sm sm:text-base leading-relaxed">
            Read the essay that names this idea:{" "}
            <Link
              to="/essays/the-human-loop"
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-1 font-display text-primary underline underline-offset-4 hover:text-primary/80 transition-colors"
            >
              The Human Loop
              <ArrowRight className="h-3 w-3" />
            </Link>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-16 sm:py-40 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center opacity-40 pointer-events-none"
        style={{ backgroundImage: `url(${ctaBg})` }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-foreground/70 pointer-events-none"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, hsl(var(--primary) / 0.18) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />
      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-3xl mx-auto text-center space-y-10 transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <h2
          className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          style={{ textWrap: 'balance' }}
        >
          What <span className="text-primary italic">now?</span>
        </h2>
        <p className="text-background/80 text-lg sm:text-xl leading-relaxed font-normal max-w-xl mx-auto">
          One conversation is where it begins.
        </p>
        <div className="pt-2">
          <Button
            onClick={handleScheduleCall}
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg px-10 py-7 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-primary/20 hover:scale-105 group"
          >
            Begin a Conversation
            <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

const AITransition = () => {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI Transformation: The Human Side of AI Deployment',
      provider: { '@type': 'Organization', name: 'ParadoxEdge', url: 'https://www.paradoxedge.com' },
      serviceType: ['AI Transformation', 'AI Adoption', 'Change Management', 'Leadership Development', 'Organizational Development'],
      description:
        'Agents are in production and judgment is the scarce input. ParadoxEdge develops the engineers building these systems and the leaders accountable for what they do.',
      url: 'https://www.paradoxedge.com/ai-transformation',
      areaServed: 'Worldwide',
      audience: { '@type': 'BusinessAudience', audienceType: 'Companies bringing AI into their work' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.paradoxedge.com/' },
        { '@type': 'ListItem', position: 2, name: 'AI Transformation', item: 'https://www.paradoxedge.com/ai-transformation' },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="AI Transformation | The Human Side of AI Deployment | ParadoxEdge"
        description="Agents are in production. Judgment and accountability are the gap. ParadoxEdge develops the engineers building the systems and the leaders answering for them."
        canonicalUrl="/ai-transformation"
        keywords="AI accountability, AI governance, agentic AI, AI adoption, human side of AI, AI transformation, leadership development"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="w-full max-w-full overflow-hidden sm:overflow-visible">
          <ElfsightWidget constrainMobile />
        </div>
        <main>
          <Hero />
          <TheStall />
          <TheWork />
          <FinalCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default AITransition;